import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService, ApiResponse } from './auth.service';
import { environment } from '../../environments/environment';

interface PasswordRecoveryRequest {
  to: string;
  from: string;
  subject: string;
  content: string;
  expiredTime: number;
  gRecaptchaAction: string;
  gRecaptchaToken: string;
  gRecaptchaSiteKey: string;
}

@Injectable({
  providedIn: 'root'
})
export class PasswordRecoveryService {
  private readonly defaultExpiryHours = 3;
  private readonly recaptchaSiteKey = environment.recaptchaSiteKey || '';
  private readonly defaultAction = 'password_recovery';

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  requestPasswordRecovery(email: string, recaptchaToken: string): Observable<boolean> {
    const normalizedEmail = (email || '').trim();
    if (!normalizedEmail) {
      return throwError(() => new Error('E-Mail field cannot be empty'));
    }
    if (!recaptchaToken) {
      return throwError(() => new Error('Recaptcha token is missing'));
    }

    const baseUrl = this.authService.getTncApiBaseUrl() || this.authService.getDefaultTncApiBaseUrl();
    const endpoint = `${baseUrl.replace(/\/+$/u, '')}/password-recovery`;

    const body: PasswordRecoveryRequest = {
      to: normalizedEmail,
      from: '',
      subject: 'Password Recovery',
      content: '',
      expiredTime: this.defaultExpiryHours,
      gRecaptchaAction: this.defaultAction,
      gRecaptchaToken: recaptchaToken,
      gRecaptchaSiteKey: this.recaptchaSiteKey
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'g-recaptcha-token': recaptchaToken,
      'g-recaptcha-site-key': this.recaptchaSiteKey,
      'g-recaptcha-action': this.defaultAction
    });

    return this.http.post<ApiResponse<boolean>>(endpoint, body, { headers }).pipe(
      map(response => {
        if (typeof response?.payload === 'boolean') {
          return response.payload;
        }
        if (response?.error?.message) {
          throw new Error(response.error.message);
        }
        return false;
      }),
      catchError(error => {
        const message =
          error?.error?.error?.message ||
          error?.error?.message ||
          error?.message ||
          'Unable to start password recovery';
        return throwError(() => new Error(message));
      })
    );
  }
}
